/**
 * PricingPage.tsx — ɳSentry pricing
 *
 * Purpose:    Pricing cards, plugin list, comparison table, and FAQ
 * Inputs:     none (static data)
 * Outputs:    Full pricing page with ɳSentry Bundle vs ɳSelf+ cards + feature comparison
 * Constraints: React Router Link for internal navigation
 * SOT:        T-P3-E3-W2-S5-T01
 */
import { Link } from 'react-router-dom'

function CheckIcon() {
  return (
    <svg
      className="h-4 w-4 flex-shrink-0 text-emerald-700 dark:text-emerald-400"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
    </svg>
  )
}

const BASELINE_PLUGINS = [
  { slug: 'nself-uptime-monitor', description: 'HTTP/TCP uptime checks with incident triggers (port 3831)' },
  { slug: 'nself-status-page', description: 'Public and private status pages for your services (port 3832)' },
  { slug: 'nself-incident-mgmt', description: 'Incident lifecycle management and post-mortems (port 3833)' },
  { slug: 'nself-alert-router', description: 'Multi-channel alert routing: Slack, email, PagerDuty, webhooks (port 3834)' },
  { slug: 'nself-slo-tracker', description: 'SLO compliance dashboards and error budget tracking (port 3835)' },
  { slug: 'nself-synthetic-monitor', description: 'Scripted browser uptime and workflow checks (port 3836)' },
  { slug: 'nself-rum', description: 'Real-user monitoring: Core Web Vitals, session replay (port 3837)' },
]

const EXPANSION_PLUGINS = [
  { slug: 'nself-errors', description: 'Error tracking with stack traces and release attribution (port 3838)' },
  { slug: 'nself-cron-monitor', description: 'Cron job heartbeat monitoring with missed-run alerts (port 3839)' },
  { slug: 'nself-oncall', description: 'On-call rotations and escalation policies (port 3840)' },
  { slug: 'nself-crash', description: 'Mobile and desktop crash reporting (port 3841)' },
  { slug: 'nself-anomaly', description: 'Anomaly detection on metrics and error rates (port 3842)' },
  { slug: 'nself-audit', description: 'Audit log for all admin and infrastructure actions — FREE tier (port 3843)' },
]

const BUNDLE_FEATURES = [
  'All 7 baseline plugins included',
  '6 expansion plugins (ɳSentry Bundle)',
  'nself-audit is free in all tiers',
  'Status pages: public or private',
  'Incident management with post-mortems',
  'SLO tracking and error budgets',
  'Uptime checks: HTTP, TCP, scripted browser',
  'Real-user monitoring (Core Web Vitals)',
  'Multi-channel alert routing',
  'Self-hosted — no data leaves your server',
  'Distinct from free Prometheus/Grafana/Loki infra monitoring',
  'Drop-in alternative to Sentry at $0.99/mo vs $26+/mo',
]

const PLUS_EXTRAS = [
  'Everything in ɳSentry Bundle',
  'ɳClaw Bundle: AI assistant',
  'ɳChat Bundle: group video, bots, messaging',
  'ɳTV Bundle: media server',
  'ɳFamily Bundle: private family social network',
  'ClawDE Bundle: AI dev environment',
  'Support via chat.nself.org',
]

export function PricingPage() {
  return (
    <>
      {/* Hero */}
      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8 text-center">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50 dark:bg-indigo-950/40 px-3 py-1 text-sm text-indigo-700 dark:text-indigo-300">
          Coming soon — join the waitlist
        </div>
        <h1 className="mb-4 text-4xl font-bold sm:text-5xl">Simple pricing</h1>
        <p className="text-lg text-brand-muted">
          13 observability plugins, one bundle. No per-seat pricing. No data leaves your server.
        </p>
      </section>

      {/* Free infra monitoring callout */}
      <section className="pb-6">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-950/20 px-6 py-4 flex items-start gap-3">
            <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-700 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
            </svg>
            <p className="text-sm text-emerald-700 dark:text-emerald-200">
              <span className="font-semibold">Infrastructure monitoring is always free.</span>{' '}
              Prometheus, Grafana, Loki, and the full monitoring stack ship with every ɳSelf instance at no cost.
              ɳSentry adds <em>product-layer</em> observability on top — status pages your customers can see, incident workflows, SLOs, and RUM.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="pb-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2">
            {/* ɳSentry Bundle */}
            <div className="rounded-xl border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50 dark:bg-indigo-950/20 p-8">
              <div className="mb-4 inline-flex items-center rounded-full border border-indigo-200 dark:border-indigo-700/50 bg-indigo-50 dark:bg-indigo-950/60 px-2.5 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-300">
                ɳSentry Bundle
              </div>
              <h2 className="text-xl font-bold text-brand-ink">ɳSentry</h2>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-brand-ink">$0.99</span>
                <span className="text-brand-muted">/mo</span>
              </div>
              <p className="mt-1 text-xs text-brand-muted">or $9.99/yr (save ~16%)</p>
              <p className="mt-3 text-sm text-brand-muted">
                All 13 ɳSentry plugins on your ɳSelf instance. Backend-only — no companion app needed.
              </p>
              <ul className="mt-6 space-y-3">
                {BUNDLE_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-brand-secondary">
                    <CheckIcon />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/invite"
                className="mt-8 block w-full rounded-md bg-sky-500 px-4 py-2.5 text-center text-sm font-medium text-white hover:bg-sky-400 transition-colors"
              >
                Join waitlist (ships v1.1.0)
              </Link>
            </div>

            {/* ɳSelf+ */}
            <div className="rounded-xl border border-brand-subtle bg-brand-surface/30 p-8">
              <div className="mb-4 inline-flex items-center rounded-full border border-brand-card/50 bg-brand-surface/60 px-2.5 py-0.5 text-xs font-medium text-brand-muted">
                Best value
              </div>
              <h2 className="text-xl font-bold text-brand-ink">ɳSelf+</h2>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-brand-ink">$3.99</span>
                <span className="text-brand-muted">/mo</span>
              </div>
              <p className="mt-1 text-xs text-brand-muted">or $39.99/yr (save ~16%)</p>
              <p className="mt-3 text-sm text-brand-muted">
                All 6 bundles + all apps + support. Cheaper than buying bundles separately.
              </p>
              <ul className="mt-6 space-y-3">
                {PLUS_EXTRAS.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-brand-secondary">
                    <CheckIcon />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="https://nself.org/pricing"
                className="mt-8 block w-full rounded-md border border-brand-card px-4 py-2.5 text-center text-sm font-medium text-brand-secondary hover:bg-brand-surface-2 hover:text-brand-ink transition-colors"
              >
                See ɳSelf+ on nself.org
              </a>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-brand-muted">
            ɳSentry Bundle is part of ɳSelf+. If you already subscribe to ɳSelf+, ɳSentry is included at no extra cost when it ships.
          </p>
          <p className="mt-4 text-center text-xs text-brand-ink/60">Prices exclude applicable VAT.</p>
        </div>
      </section>

      {/* Plugin list — baseline */}
      <section className="border-t border-brand-subtle bg-brand-surface/30 py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-2 text-2xl font-bold">Baseline plugins (7)</h2>
          <p className="mb-8 text-sm text-brand-muted">
            Core observability. Installed via{' '}
            <code className="rounded bg-brand-surface-2 px-1.5 py-0.5 font-mono text-xs text-indigo-700 dark:text-indigo-300">
              nself plugin install
            </code>{' '}
            once you have a valid license key.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {BASELINE_PLUGINS.map((p) => (
              <div key={p.slug} className="rounded-lg border border-brand-subtle bg-brand-bg p-4">
                <p className="mb-1 font-mono text-sm font-medium text-indigo-700 dark:text-indigo-300">{p.slug}</p>
                <p className="text-xs text-brand-muted">{p.description}</p>
              </div>
            ))}
          </div>

          <h2 className="mb-2 mt-12 text-2xl font-bold">Expansion plugins (6)</h2>
          <p className="mb-8 text-sm text-brand-muted">
            Advanced observability. All included in the ɳSentry Bundle. nself-audit is free in every tier.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {EXPANSION_PLUGINS.map((p) => (
              <div key={p.slug} className="rounded-lg border border-brand-subtle bg-brand-bg p-4">
                <p className="mb-1 font-mono text-sm font-medium text-indigo-700 dark:text-indigo-300">{p.slug}</p>
                <p className="text-xs text-brand-muted">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="border-t border-brand-subtle bg-brand-surface/30 py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-3 text-2xl font-bold">How ɳSentry compares</h2>
          <p className="mb-8 text-sm text-brand-muted">
            Same observability power, fraction of the cost. Self-hosted. Forever yours.
          </p>
          <div className="rounded-lg border border-brand-subtle bg-brand-bg p-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-brand-subtle">
                    <th className="px-3 py-3 text-left font-semibold text-brand-secondary">Feature</th>
                    <th className="px-3 py-3 text-left font-semibold text-indigo-700 dark:text-indigo-300">ɳSentry</th>
                    <th className="px-3 py-3 text-left font-semibold text-brand-muted">Sentry SaaS</th>
                    <th className="px-3 py-3 text-left font-semibold text-brand-muted">Datadog APM</th>
                    <th className="px-3 py-3 text-left font-semibold text-brand-muted">PagerDuty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  <tr className="hover:bg-brand-surface/40">
                    <td className="px-3 py-3 font-medium text-brand-secondary">Cost (monthly)</td>
                    <td className="px-3 py-3 text-indigo-700 dark:text-indigo-300 font-semibold">$0.99</td>
                    <td className="px-3 py-3 text-brand-muted">$26+</td>
                    <td className="px-3 py-3 text-brand-muted">$31/host</td>
                    <td className="px-3 py-3 text-brand-muted">$19/user</td>
                  </tr>
                  <tr className="hover:bg-brand-surface/40">
                    <td className="px-3 py-3 font-medium text-brand-secondary">Hosting</td>
                    <td className="px-3 py-3 text-indigo-700 dark:text-indigo-300">Self-hosted</td>
                    <td className="px-3 py-3 text-brand-muted">SaaS</td>
                    <td className="px-3 py-3 text-brand-muted">SaaS</td>
                    <td className="px-3 py-3 text-brand-muted">SaaS</td>
                  </tr>
                  <tr className="hover:bg-brand-surface/40">
                    <td className="px-3 py-3 font-medium text-brand-secondary">Data retention</td>
                    <td className="px-3 py-3 text-indigo-700 dark:text-indigo-300">Unlimited</td>
                    <td className="px-3 py-3 text-brand-muted">30 days</td>
                    <td className="px-3 py-3 text-brand-muted">15 days</td>
                    <td className="px-3 py-3 text-brand-muted">—</td>
                  </tr>
                  <tr className="hover:bg-brand-surface/40">
                    <td className="px-3 py-3 font-medium text-brand-secondary">Self-host option</td>
                    <td className="px-3 py-3 text-indigo-700 dark:text-indigo-300">✓</td>
                    <td className="px-3 py-3 text-brand-muted">✗</td>
                    <td className="px-3 py-3 text-brand-muted">✗</td>
                    <td className="px-3 py-3 text-brand-muted">✗</td>
                  </tr>
                  <tr className="hover:bg-brand-surface/40">
                    <td className="px-3 py-3 font-medium text-brand-secondary">Open source</td>
                    <td className="px-3 py-3 text-indigo-700 dark:text-indigo-300">Yes (MIT)</td>
                    <td className="px-3 py-3 text-brand-muted">No</td>
                    <td className="px-3 py-3 text-brand-muted">No</td>
                    <td className="px-3 py-3 text-brand-muted">No</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-10 text-center text-2xl font-bold">Common questions</h2>
          <div className="space-y-8">
            {[
              {
                q: 'When does ɳSentry ship?',
                a: 'ɳSentry is planned for v1.1.0. Join the waitlist and you will get an invite code and pricing notification when the release date is confirmed.',
              },
              {
                q: 'Does ɳSentry replace Prometheus and Grafana?',
                a: 'No. Prometheus, Grafana, Loki, and the full infra monitoring stack are free in every ɳSelf instance. ɳSentry adds product-layer observability on top: status pages your customers can see, incident management, SLOs, and real-user monitoring. They work side by side.',
              },
              {
                q: 'How is this different from Sentry.io?',
                a: 'Sentry.io is a hosted SaaS starting at $26/mo. ɳSentry runs on your VPS — you own the data, there is no per-event pricing, and the full bundle costs $0.99/mo. You install it the same way as any other nSelf plugin.',
              },
              {
                q: 'Do I need a companion app?',
                a: 'No. ɳSentry is backend-only. You manage it via the nSelf Admin UI and access status pages via your browser. No Flutter app is required.',
              },
              {
                q: 'What if I cancel?',
                a: 'If you cancel the bundle, ɳSentry plugins go dormant on your next nself build. Your data stays on your server. nself-audit remains active (it is always free). You can re-activate anytime.',
              },
            ].map((item) => (
              <div key={item.q}>
                <h3 className="mb-2 font-semibold text-brand-ink">{item.q}</h3>
                <p className="text-sm text-brand-muted">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
